// ante-react-kit/src/components/AntQuickPopup/AntQuickPopup.js
import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';
import useQuickPopup from './useQuickPopup';
import { QuickPopupProvider } from './QuickPopupContext';
import AntLocalization from '../../utils/AntLocalization';
import './AntQuickPopup.scss';

const SIZE_MAP = {
    sm: { width: 480, height: 'auto' },
    md: { width: 720, height: 'auto' },
    lg: { width: 960, height: 720 },
    xl: { width: 1200, height: 800 },
    fullscreen: { width: '95vw', height: '95vh' }
};

export default function AntQuickPopup(props) {
    const {
        api, id, code,
        visible, onClose,
        title,
        size = 'md',
        resizable = true,
        draggable = true,
        showFullEditLink = true,
        fullEditLink,
        footerType = 'auto',
        footerRender,
        saveMode = 'manual',
        saveDebounceMs = 500,
        confirmDiscardChanges = true,
        session, lookupsFE, editors, fieldsCnf,
        onSaved, onActionDone, onDirtyChange,
        children,
        className, popupClassName
    } = props;

    const qp = useQuickPopup(api, {
        id, code,
        session, lookupsFE, editors, fieldsCnf
    });

    // Notify dirty change
    useEffect(() => {
        if (onDirtyChange) onDirtyChange(qp.dirty);
    }, [qp.dirty, onDirtyChange]);

    // Resolve title
    const resolvedTitle = useMemo(() => {
        if (typeof title === 'function') return title({ data: qp.data });
        if (title) return AntLocalization.translateText(title) || title;
        return (qp.data && (qp.data.name || qp.data.code)) || '';
    }, [title, qp.data]);

    // Auto-blur save mode (debounced)
    const saveTimerRef = useRef(null);
    useEffect(() => {
        if (saveMode !== 'auto-blur' && saveMode !== 'auto-debounce') return;
        if (!qp.dirty) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(async () => {
            const r = await qp.save();
            if (r.saved && onSaved) onSaved(r.data);
        }, saveDebounceMs);
        return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
    }, [qp.dirty, saveMode, saveDebounceMs, qp, onSaved]);

    // Close handler with dirty confirm
    const handleClose = useCallback(() => {
        if (qp.dirty && confirmDiscardChanges) {
            const ok = window.confirm(AntLocalization.translateText({
                vi: 'Các thay đổi chưa lưu sẽ mất. Đóng popup?',
                en: 'Unsaved changes will be lost. Close?',
                fr: 'Modifications non enregistrées perdues. Fermer ?',
                es: 'Se perderán los cambios sin guardar. ¿Cerrar?',
                id: 'Perubahan belum disimpan akan hilang. Tutup popup?',
                ja: '未保存の変更は失われます。閉じますか?',
                zh: '未保存的更改将丢失。关闭弹窗?',
                ko: '저장 안 된 변경이 사라집니다. 닫을까요?',
                hi: 'बिना सहेजे बदलाव मिट जाएँगे। बंद करें?',
                ar: 'ستُفقد التغييرات غير المحفوظة. إغلاق؟'
            }));
            if (!ok) return;
        }
        if (onClose) onClose();
    }, [qp.dirty, confirmDiscardChanges, onClose]);

    // Manual save
    const handleSave = useCallback(async () => {
        const r = await qp.save();
        if (r.saved && onSaved) onSaved(r.data);
    }, [qp, onSaved]);

    // Full edit navigate
    const handleFullEdit = useCallback(() => {
        if (!fullEditLink) return;
        if (qp.dirty && !window.confirm(AntLocalization.translateText({
            vi: 'Các thay đổi chưa lưu sẽ mất. Mở phiếu đầy đủ?',
            en: 'Unsaved changes will be lost. Open full editor?',
            fr: 'Modifications non enregistrées perdues. Ouvrir en entier ?',
            es: 'Se perderán los cambios sin guardar. ¿Abrir completo?',
            id: 'Perubahan belum disimpan akan hilang. Buka tampilan penuh?',
            ja: '未保存の変更は失われます。全画面で開きますか?',
            zh: '未保存的更改将丢失。打开完整单据?',
            ko: '저장 안 된 변경이 사라집니다. 전체 전표를 열까요?',
            hi: 'बिना सहेजे बदलाव मिट जाएँगे। पूरा वाउचर खोलें?',
            ar: 'ستُفقد التغييرات غير المحفوظة. فتح السند كاملاً؟'
        }))) return;
        const link = typeof fullEditLink === 'function' ? fullEditLink({ data: qp.data }) : fullEditLink;
        const target = link + (link.endsWith('/') ? '' : '/') + (qp.data?.code || '');
        if (typeof window !== 'undefined') window.location.assign(target);
    }, [fullEditLink, qp.data, qp.dirty]);

    // Context value provided to children
    const contextValue = useMemo(() => ({
        ...qp,
        api,
        onCloseRequest: handleClose,
        onActionDone,
        saveMode
    }), [qp, api, handleClose, onActionDone, saveMode]);

    const sz = SIZE_MAP[size] || SIZE_MAP.md;

    return (
        <Popup
            visible={!!visible}
            onHiding={handleClose}
            dragEnabled={draggable}
            resizeEnabled={resizable}
            showCloseButton={false}
            hideOnOutsideClick={false}
            width={sz.width}
            height={sz.height}
            wrapperAttr={{ class: 'ant-quick-popup-wrapper ' + (popupClassName || '') }}
            showTitle={false}
        >
            <div className={'ant-quick-popup ' + (className || '')}>
                <div className="aqp-header">
                    <div className="aqp-title">{resolvedTitle}</div>
                    <div className="aqp-header-actions">
                        {showFullEditLink && fullEditLink && (
                            <button className="aqp-icon-btn" title={AntLocalization.translateText({ vi: 'Mở phiếu đầy đủ', en: 'Open full', fr: 'Ouvrir en entier', es: 'Abrir completo', id: 'Buka penuh', ja: '全画面で開く', zh: '打开完整单据', ko: '전체 전표 열기', hi: 'पूरा वाउचर खोलें', ar: 'فتح السند كاملاً' })} onClick={handleFullEdit}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                                </svg>
                            </button>
                        )}
                        <button className="aqp-icon-btn aqp-close" onClick={handleClose} title={AntLocalization.translateText({ vi: 'Đóng', en: 'Close', fr: 'Fermer', es: 'Cerrar', id: 'Tutup', ja: '閉じる', zh: '关闭', ko: '닫기', hi: 'बंद करें', ar: 'إغلاق' })}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="aqp-body">
                    {qp.isLoading && !qp.data && <div className="aqp-loading">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा...', ar: 'تحميل...' })}</div>}
                    {qp.error && <div className="aqp-error">&#9888; {String(qp.error.message || qp.error)} <button onClick={qp.reload}>{AntLocalization.translateText({ vi: 'Thử lại', en: 'Retry', fr: 'Réessayer', es: 'Reintentar', id: 'Coba lagi', ja: '再試行', zh: '重试', ko: '다시 시도', hi: 'फिर करें', ar: 'إعادة' })}</button></div>}
                    {qp.data && (
                        <QuickPopupProvider value={contextValue}>
                            {typeof children === 'function' ? children(contextValue) : children}
                        </QuickPopupProvider>
                    )}
                </div>

                {footerType !== 'none' && qp.data && (
                    <div className="aqp-footer">
                        {footerType === 'custom' && footerRender && footerRender(contextValue)}
                        {footerType === 'save-cancel' || (footerType === 'auto' && saveMode === 'manual') ? (
                            <React.Fragment>
                                <Button text={AntLocalization.translateText({ vi: 'Huỷ', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', id: 'Batal', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء', ja: '取消' })} onClick={handleClose} />
                                <Button
                                    text={AntLocalization.translateText({ vi: 'Lưu', en: 'Save', es: 'Guardar', id: 'Simpan', ja: '保存', zh: '保存', ko: '저장', hi: 'सहेजें', ar: 'حفظ', fr: 'Enreg.' })}
                                    type="default"
                                    disabled={!qp.dirty}
                                    onClick={handleSave}
                                />
                            </React.Fragment>
                        ) : null}
                    </div>
                )}
            </div>
        </Popup>
    );
}
